import IconButton from '@betterfunnel/ui-core/IconButton';
import Select from '@betterfunnel/ui-core/Select';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import Tooltip from '@betterfunnel/ui-core/Tooltip';
import {
    AssetData,
    CTA,
    FormInWebsite,
    SectionData,
    SectionTypeStructureField,
    SectionTypeStructureFieldBoolean,
    SectionTypeStructureFieldCta,
    SectionTypeStructureFieldCtaList,
    SectionTypeStructureFieldCustomCode,
    SectionTypeStructureFieldForm,
    SectionTypeStructureFieldImage,
    SectionTypeStructureFieldList,
    SectionTypeStructureFieldLiteral,
    SectionTypeStructureFieldNumber,
    SectionTypeStructureFieldObject,
    SectionTypeStructureFieldString,
    SectionTypeStructureFieldText,
    TextOrQuillText,
    WithId,
} from '@betterfunnel/types';
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Transition,
} from '@headlessui/react';
import {
    ChevronRightIcon,
    EyeIcon,
    EyeSlashIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { useContext, useMemo } from 'react';
import { uid } from 'uid';
import { AISuggestionsContext } from '../../contexts/AISuggestionsContextProvider';
import { camelCaseToTitle, capitalize, classNames } from '../../utils';
import { extractTextFromQuillTrimmed } from '../../utils/quill';
import {
    formFieldEmailValidator,
    formFieldUrlValidator,
} from '../../utils/validator';
import CTAListEditor from '../CTAListEditor';
import { CodeEditorTextArea } from '../FormFields/CodeEditor/CodeEditorTextArea';
import FileUploadInput from '../FormFields/FileUpload/FileUploadInput';
import { FormSelector } from '../FormFields/FormSelector';
import QuillField from '../QuillField';
import SectionItemsListEditor from '../SectionItemsListEditor';

type Content = SectionData | WithId<Record<string, any>>;

interface Props<T extends Content> {
  content: T;
  fields: SectionTypeStructureField[];
  onChange: (content: T) => void;
  isSection?: boolean;
}

export function SectionStructureForm<T extends Content>({
  content,
  fields,
  onChange,
  isSection,
}: Props<T>) {
  return (
    <div key={content.id} className="space-y-2">
      {fields?.map((field) => (
        <SectionStructureFormField
          key={field.name}
          content={content}
          field={field}
          onChange={onChange}
          isSection={isSection}
        />
      ))}
    </div>
  );
}

interface SectionStructureFormFieldProps<
  T extends Content,
  F extends SectionTypeStructureField = SectionTypeStructureField,
> {
  content: T;
  field: F;
  onChange: (content: T) => void;
  isSection?: boolean;
}

function SectionStructureFormField<T extends Content>({
  field,
  ...props
}: SectionStructureFormFieldProps<T>) {
  switch (field.type) {
    case 'text':
      return <SectionStructureFormFieldText field={field} {...props} />;

    case 'string':
      return <SectionStructureFormFieldString field={field} {...props} />;

    case 'number':
      return <SectionStructureFormFieldNumber field={field} {...props} />;

    case 'boolean':
      return <SectionStructureFormFieldBoolean field={field} {...props} />;

    case 'literal':
      return <SectionStructureFormFieldLiteral field={field} {...props} />;

    case 'ctaList':
      return <SectionStructureFormFieldCTAList field={field} {...props} />;

    case 'cta':
      return <SectionStructureFormFieldCTA field={field} {...props} />;

    case 'image':
      return <SectionStructureFormFieldImage field={field} {...props} />;

    case 'custom-code':
      return <SectionStructureFormFieldCustomCode field={field} {...props} />;

    case 'form':
      return <SectionStructureFormFieldForm field={field} {...props} />;

    case 'list':
      return <SectionStructureFormFieldList field={field} {...props} />;

    case 'object':
      return <SectionStructureFormFieldObject field={field} {...props} />;
  }

  Sentry.captureException(
    new Error(
      `Unsupported field type in SectionStructureForm: ${field.type} in ${field.name} in ${props.content.type}`,
    ),
  );
  console.error(
    `Unsupported field type in SectionStructureForm: ${field.type} in ${field.name} in ${props.content.type}`,
  );
  return <div></div>;
}

function SectionStructureFormFieldText<T extends Content>({
  content,
  field,
  onChange,
  isSection,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldText>) {
  const { fieldSuggestions } = useContext(AISuggestionsContext);

  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as TextOrQuillText | undefined;
  const hideFieldName = `hide${capitalize(field.name)}` as keyof T;

  const isHidable =
    isSection && ['title', 'subtitle', 'tagline'].includes(field.name);

  return (
    <QuillField
      key={fieldName as string}
      title={fieldTitle}
      value={fieldValue}
      onChange={(value) => {
        onChange({
          ...content,
          [fieldName]: extractTextFromQuillTrimmed(value) ? value : null,
        });
      }}
      debounce={isSection}
      readOnly={isHidable ? !!content[hideFieldName] : false}
      trailing={
        isHidable ? (
          <Tooltip
            text={content[hideFieldName] ? 'Show' : 'Hide'}
            placement="right"
          >
            <div>
              <IconButton
                variant="ternary"
                size="small"
                onClick={() => {
                  onChange({
                    ...content,
                    [hideFieldName]: !content[hideFieldName],
                  });
                }}
              >
                {content[hideFieldName] ? (
                  <EyeSlashIcon className="h-3 w-3" />
                ) : (
                  <EyeIcon className="h-3 w-3" />
                )}
              </IconButton>
            </div>
          </Tooltip>
        ) : undefined
      }
      className={
        isHidable && content[hideFieldName] ? 'bg-gray-200 opacity-50' : ''
      }
      generateSuggestions={async (textSelection, fullLine, prompt) => {
        return fieldSuggestions(
          '',
          '',
          fieldTitle,
          extractTextFromQuillTrimmed(fullLine),
          extractTextFromQuillTrimmed(textSelection),
          prompt,
        );
      }}
    />
  );
}

function SectionStructureFormFieldString<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldString>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  const validation = useMemo(() => {
    const dataType = field.dataType;

    switch (dataType) {
      case 'email':
        return formFieldEmailValidator(fieldValue, false);

      case 'url':
        return formFieldUrlValidator(fieldValue, false);
    }

    return undefined;
  }, [fieldValue, field.dataType]);

  return (
    <TextField
      key={fieldName as string}
      title={fieldTitle}
      value={fieldValue}
      onChange={(value) => {
        onChange({ ...content, [fieldName]: value?.target.value });
      }}
      validation={validation}
    />
  );
}

function SectionStructureFormFieldNumber<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldNumber>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  return (
    <TextField
      key={fieldName as string}
      title={fieldTitle}
      value={fieldValue?.toString() || ''}
      type="number"
      // input={{ max: 5, min: 0, step: 0.5 }}
      onChange={(e) => {
        onChange({ ...content, [fieldName]: +(e.target.value as any) });
      }}
    />
  );
}

function SectionStructureFormFieldBoolean<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldBoolean>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as boolean | undefined;

  return (
    <Toggle
      key={fieldName as string}
      label={fieldTitle}
      checked={fieldValue ?? false}
      onChange={(checked) => {
        onChange({ ...content, [fieldName]: checked });
      }}
    />
  );
}

function SectionStructureFormFieldLiteral<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldLiteral>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  return (
    <Select
      title={fieldTitle}
      selected={fieldValue}
      options={field.options.map((option) =>
        typeof option === 'string'
          ? {
              value: option,
              child: option,
            }
          : {
              value: option.value,
              child: option.label,
            },
      )}
      onChange={(value) => {
        onChange({ ...content, [fieldName]: value });
      }}
    />
  );
}

function SectionStructureFormFieldCTAList<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldCtaList>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || 'Buttons';
  const fieldValue = content[fieldName] as CTA[] | undefined;

  return (
    <CTAListEditor
      title={fieldTitle}
      cta={fieldValue}
      onChange={(cta) => {
        onChange({ ...content, [fieldName]: cta });
      }}
    />
  );
}

function SectionStructureFormFieldCTA<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldCta>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || 'Buttons';
  const fieldValue = content[fieldName] as CTA | undefined;

  return (
    <CTAListEditor
      disableEditAction={field.disableEditAction}
      title={fieldTitle}
      cta={fieldValue ? [fieldValue] : []}
      onChange={(cta) => {
        onChange({ ...content, [fieldName]: cta[0] || null });
      }}
    />
  );
}

function SectionStructureFormFieldImage<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldImage>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as AssetData | undefined;

  return (
    <div>
      <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
        {fieldTitle}
      </label>
      <FileUploadInput
        image={fieldValue}
        onSelected={(image) => {
          onChange({ ...content, [fieldName]: (image || null) as any });
        }}
        showImageSettings={field.imageType !== 'logo'}
        types={field.imageType === 'logo' ? ['image'] : ['image', 'video']}
      />
    </div>
  );
}

function SectionStructureFormFieldCustomCode<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldCustomCode>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as string | undefined;

  return (
    <CodeEditorTextArea
      title={fieldTitle}
      value={fieldValue ?? ''}
      onChange={(code) => {
        onChange({ ...content, [fieldName]: code });
      }}
      className="h-56"
      allowModal
    />
  );
}

// Only supports one form per content
function SectionStructureFormFieldForm<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldForm>) {
  const fieldName = 'formId' as keyof T;
  const fieldTitle = field.label || 'Form';
  const fieldValue =
    fieldName in content
      ? (content[fieldName] as string | undefined)
      : 'formData' in content
        ? (content.formData as FormInWebsite)?.formId
        : undefined;

  return (
    <FormSelector
      title={fieldTitle}
      selected={fieldValue}
      onChange={(selected) => {
        onChange({ ...content, [fieldName]: selected?.id || null });
      }}
    />
  );
}

function SectionStructureFormFieldList<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldList>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = (content[fieldName] ?? []) as WithId<
    Record<string, any>
  >[];

  return (
    <SectionItemsListEditor
      title={fieldTitle}
      items={fieldValue}
      sectionType={content.type}
      field={field}
      onChange={(items) => {
        onChange({ ...content, [fieldName]: items as any });
      }}
    />
  );
}

function SectionStructureFormFieldObject<T extends Content>({
  content,
  field,
  onChange,
}: SectionStructureFormFieldProps<T, SectionTypeStructureFieldObject>) {
  const fieldName = field.name as keyof T;
  const fieldTitle = field.label || camelCaseToTitle(field.name);
  const fieldValue = content[fieldName] as
    | WithId<Record<string, any>>
    | undefined;

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <DisclosureButton
            className={classNames('flex w-full items-center gap-1 py-2')}
          >
            <span className="block text-sm font-medium leading-6 text-gray-900">
              {fieldTitle}
            </span>
            <div className="flex-1" />
            <ChevronRightIcon
              className={classNames(
                'h-4 w-4',
                open ? 'rotate-90 transform' : '',
              )}
            />
          </DisclosureButton>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <DisclosurePanel>
              <div className="pb-3">
                <SectionStructureForm
                  content={fieldValue ?? { id: uid() }}
                  fields={field.structure.fields}
                  onChange={(value) => {
                    onChange({ ...content, [fieldName]: value as any });
                  }}
                />
              </div>
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
